// src/components/ClaimUploadModal.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { Check, X, UploadCloud, FileText, Copy, AlertTriangle } from "lucide-react";
import { motion } from "framer-motion";
import useAuth from "../hooks/useAuth";
import { initProof, uploadFileAndComplete, createClaim, abortUpload } from "../services/uploadService";
import { useNavigate } from "react-router-dom";
import { getClaimReasons, deleteClaim } from "../services/claimService";
import { API } from "../utils/apiUrls";
import { apiClient } from "../utils/apiClient";

const InlineAppLogo = ({ className = "w-8 h-8" }) => (
  <svg className={className} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <rect width="48" height="48" rx="10" fill="#EEF2FF" />
    <path d="M14 24c0-5 7-9 10-9s10 4 10 9-7 9-10 9-10-4-10-9z" fill="#4F46E5" />
    <circle cx="24" cy="24" r="3" fill="white" />
  </svg>
);

const InlineUploadIllustration = ({ className = "w-12 h-12" }) => (
  <svg className={className} viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <rect x="0" y="0" width="120" height="120" rx="12" fill="#F8FAFC" />
    <path d="M60 28v48" stroke="#0F172A" strokeWidth="4" strokeLinecap="round" />
    <path d="M44 44l16-16 16 16" stroke="#0F172A" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
    <rect x="28" y="72" width="64" height="12" rx="3" fill="#E6EEF9" />
  </svg>
);

export default function ClaimUploadModal({ open, onClose, shipment = null, defaultItem = null, claimType = "damage" }) {
  const { getMerchantId, getAuthToken } = useAuth();
  const navigate = useNavigate();

  // form fields
  const [merchantId, setMerchantId] = useState("");
  const [authToken, setAuthToken] = useState("");
  const [awbNumber, setAwbNumber] = useState("");
  const [orderReferenceNumber, setOrderReferenceNumber] = useState("");
  const items = useMemo(() => (shipment?.shipment_packages ?? []).flatMap(p => p.items ?? []), [shipment]);

  // multiple-select items
  const [selectedItems, setSelectedItems] = useState([]);
  const [claimDescription, setClaimDescription] = useState("");

  // files
  const [unboxingFile, setUnboxingFile] = useState(null);
  const [packagingFile, setPackagingFile] = useState(null);
  const [podFile, setPodFile] = useState(null);

  // UX
  const [busy, setBusy] = useState(false);
  const [overallPct, setOverallPct] = useState(0);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [successPayload, setSuccessPayload] = useState(null);

  const [claimReasonCode, setClaimReasonCode] = useState(null);
  const [claimReasonsList, setClaimReasonsList] = useState([]);
  const [reasonsLoading, setReasonsLoading] = useState(false);
  const [reasonsError, setReasonsError] = useState(null);
  const [fileUrls, setFileUrls] = useState({ unboxing: null, packaging: null, pod: null });
  const videoRef = useRef(null);


  // per-file status (keeps track for chips)
  const [fileStatus, setFileStatus] = useState({
    unboxing: { status: "idle", pct: 0, message: "" },
    packaging: { status: "idle", pct: 0, message: "" },
    pod: { status: "idle", pct: 0, message: "" },
  });

  const [oversizeModal, setOversizeModal] = useState({ open: false, key: null, file: null, message: "" });
  const abortControllerRef = useRef(null);
  const currentClaimIdRef = useRef(null);
  const uploadStartRef = useRef(null);
  const lastPctRef = useRef(0); // for ETA heuristic

  // confirm modal
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Result modal (success / error) - top-level dialog (separate from the form dialog)
  const [resultModalOpen, setResultModalOpen] = useState(false);
  const [resultKind, setResultKind] = useState("success"); // "success" | "error"
  const [resultData, setResultData] = useState(null); // claim/proofs or error object
  const [resultCountdown, setResultCountdown] = useState(5);

  // limits
  const MAX_BYTES = 150 * 1024 * 1024; // 150MB
  const VIDEO_TYPES = ["video/mp4", "video/quicktime", "video/webm", "video/x-msvideo"];
  const IMAGE_TYPES = ["image/png", "image/jpeg", "image/jpg"];
  const POD_TYPES = [...IMAGE_TYPES, "application/pdf"];



  useEffect(() => {
    if (!open) return;
    setMerchantId(getMerchantId() || "");
    setAuthToken(getAuthToken() || "");
    setAwbNumber(shipment?.awb_number ?? "");
    setOrderReferenceNumber(shipment?.order_reference_number ?? "");
    setSelectedItems(defaultItem ? [defaultItem] : []);
    setUnboxingFile(null);
    setPackagingFile(null);
    setPodFile(null);
    setClaimDescription("");
    setError("");
    setSuccessMessage("");
    setBusy(false);
    setOverallPct(0);
    setSuccessPayload(null);
    setClaimDescription(claimType === "weight_dispute" ? "Shipment Dimension Proof" : "");
    currentClaimIdRef.current = null;
    uploadStartRef.current = null;
    lastPctRef.current = 0;
    setFileStatus({
      unboxing: { status: 'idle', pct: 0, message: '' },
      packaging: { status: 'idle', pct: 0, message: '' },
      pod: { status: 'idle', pct: 0, message: '' },
    });
    setConfirmOpen(false);
    setResultModalOpen(false);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, [open, shipment, defaultItem, getMerchantId, getAuthToken, claimType]);

  const cleanupPersistedInits = async (merchantId) => {
    try {
      ['unboxing', 'packaging', 'pod'].forEach(k => clearPersist(k));
    } catch (e) {
      console.warn('cleanupPersistedInits failed', e);
    }
  };

  const cleanupPartialClaim = async (claimId) => {
    if (!claimId) return;
    try {
      // Abort each persisted init (if any)
      for (const key of ['unboxing', 'packaging', 'pod']) {
        try {
          const raw = localStorage.getItem(`claim_upload_${merchantId}_${key}`);
          if (!raw) continue;
          const parsed = JSON.parse(raw);
          // attempt to extract uploadId or initResp fields (uploadId or upload_id)
          const uploadId = parsed?.initResp?.uploadId ?? parsed?.initResp?.upload_id ?? parsed?.initResp?.uploadId;
          const objectKey = parsed?.initResp?.key ?? parsed?.initResp?.object_key ?? parsed?.initResp?.key;

          try {
            if (uploadId) {
              await abortUpload({ uploadId, merchantId });
            } else {
              await abortUpload({ merchant_id: merchantId, claim_id: claimId });
            }
          } catch (e) {
            console.warn('abort for init failed (best-effort) key=', key, e?.message ?? e);
          }

          clearPersist(key);
        } catch (e) {
          console.warn('failed to abort persisted init for key', key, e);
        }
      }

      try {
        await deleteClaim(claimId, merchantId);
      } catch (e) {
        console.warn('deleteClaim failed (best-effort)', e?.message ?? e);
      }

      // Clear local persisted stores
      await cleanupPersistedInits(merchantId);
    } finally {
      currentClaimIdRef.current = null;
    }
  };

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    const controller = new AbortController();
    (async () => {
      try {
        setReasonsLoading(true);
        setReasonsError(null);
        const data = await getClaimReasons({ signal: controller.signal });
        if (!mounted) return;
        setClaimReasonsList(Array.isArray(data) ? data : []);
        setClaimReasonCode(prev => prev ?? (data?.[0]?.code ?? null));
      } catch (err) {
        if (err?.name === 'CanceledError' || err?.message === 'canceled') return;
        console.warn("getClaimReasons failed", err);
        if (mounted) setReasonsError(err);
      } finally {
        if (mounted) setReasonsLoading(false);
      }
    })();
    return () => { mounted = false; controller.abort(); };
  }, [open]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onUserClick = () => {
      try { v.muted = false; } catch (e) { /* ignore */ }
      v.removeEventListener('click', onUserClick);
    };
    v.addEventListener('click', onUserClick);
    return () => v.removeEventListener('click', onUserClick);
  }, []);

  const humanFileSize = (bytes) => {
    if (!bytes) return "0 B";
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };
  const fmtDuration = (s) => {
    if (!s && s !== 0) return '';
    const mm = Math.floor(s / 60);
    const ss = Math.floor(s % 60).toString().padStart(2, '0');
    return `${mm}:${ss}`;
  };
  const setFileStatusSafe = (key, patch) => setFileStatus(prev => ({ ...prev, [key]: { ...(prev[key] || {}), ...patch } }));

  const persistInit = (key, initResp) => { try { localStorage.setItem(`claim_upload_${merchantId}_${key}`, JSON.stringify({ initResp, createdAt: Date.now() })); } catch (e) { } };
  const clearPersist = (key) => { try { localStorage.removeItem(`claim_upload_${merchantId}_${key}`); } catch (e) { } };

  const probeVideoDuration = (file, key) => {
    if (!file || !file.type.startsWith('video/')) return;
    try {
      const url = URL.createObjectURL(file);
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.src = url;
      video.onloadedmetadata = () => { setFileStatusSafe(key, { duration: fmtDuration(video.duration) }); URL.revokeObjectURL(url); };
      setTimeout(() => { try { URL.revokeObjectURL(url); } catch (e) { } }, 30000);
    } catch (e) { }
  };

  const validateFile = (file, key) => {
    if (!file) return { ok: true };
    if (file.size > MAX_BYTES) return { ok: false, code: 'TOO_LARGE', message: `${humanFileSize(file.size)} — exceeds maximum allowed ${humanFileSize(MAX_BYTES)}.` };
    const allowed = (key === 'pod') ? POD_TYPES.concat(VIDEO_TYPES) : VIDEO_TYPES;
    if (allowed && !allowed.includes(file.type)) return { ok: false, code: 'BAD_TYPE', message: `Unsupported file type: ${file.type}` };
    return { ok: true };
  };

  const makeHandleFileSelect = (setter, key) => async (e) => {
    const file = e.target.files?.[0] ?? null;
    if (!file) { setter(null); setFileStatusSafe(key, { status: 'idle', pct: 0, message: '' }); return; }
    const v = validateFile(file, key);
    if (v.ok) {
      setter(file);
      if (fileUrls[key]) {
        try { URL.revokeObjectURL(fileUrls[key]); } catch (e) { }
      }
      const newUrl = URL.createObjectURL(file);
      setFileUrls(prev => ({ ...prev, [key]: newUrl }));
      setFileStatusSafe(key, { status: 'selected', pct: 0, message: '' });
      probeVideoDuration(file, key);
      return;
    }

    if (v.code === 'TOO_LARGE') { setter(file); setFileStatusSafe(key, { status: 'oversized', pct: 0, message: v.message }); setOversizeModal({ open: true, key, file, message: v.message }); return; }
    setter(null); setFileStatusSafe(key, { status: 'error', pct: 0, message: v.message });
  };

  const handleDrop = (setter) => (e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0] ?? null; if (f) setter(f); };
  const previewFile = (file) => { if (!file) return; const url = URL.createObjectURL(file); window.open(url, '_blank'); };

  const removeFile = (key) => {
    if (fileUrls[key]) {
      try { URL.revokeObjectURL(fileUrls[key]); } catch (e) { }
    }
    setFileUrls(prev => ({ ...prev, [key]: null }));
    if (key === 'unboxing') setUnboxingFile(null);
    if (key === 'packaging') setPackagingFile(null);
    if (key === 'pod') setPodFile(null);
    setFileStatus(prev => ({ ...prev, [key]: { status: 'idle', pct: 0, message: '' } }));
    clearPersist(key);
  };

  const handleRetry = (key) => setFileStatusSafe(key, { status: 'idle', pct: 0, message: 'Retry requested' });

  const approveOversize = () => {
    const { key, file } = oversizeModal;
    setOversizeModal({ open: false, key: null, file: null, message: '' });
    setFileStatusSafe(key, { status: 'oversize-override', message: 'User accepted oversized upload' });
    probeVideoDuration(file, key);
  };
  const cancelOversize = () => {
    const { key } = oversizeModal;
    if (key === 'unboxing') setUnboxingFile(null);
    if (key === 'packaging') setPackagingFile(null);
    if (key === 'pod') setPodFile(null);
    setFileStatusSafe(key, { status: 'idle', pct: 0, message: '' });
    setOversizeModal({ open: false, key: null, file: null, message: '' });
  };

  // multi-item helpers
  const toggleSelectItem = (it) => {
    setSelectedItems(prev => {
      const next = [...prev];
      const key = it?.item_general_details?.sku + '|' + (it?.item_general_details?.item_name ?? '');
      const idx = next.findIndex(x => (x?.item_general_details?.sku + '|' + (x?.item_general_details?.item_name ?? '')) === key);
      if (idx >= 0) next.splice(idx, 1); else next.push(it);
      return next;
    });
  };
  const isItemSelected = (it) => {
    const key = it?.item_general_details?.sku + '|' + (it?.item_general_details?.item_name ?? '');
    return selectedItems.some(x => (x?.item_general_details?.sku + '|' + (x?.item_general_details?.item_name ?? '')) === key);
  };

  const validateForm = () => {
    if (!authToken) return "Authentication expired. Please login again.";
    if (!merchantId) return "Merchant ID missing. Please re-login.";
    if (!awbNumber) return "AWB number is required.";

    // For weight_dispute we relax item selection and unboxing requirement
    if (claimType !== "weight_dispute") {
      if (!selectedItems || selectedItems.length === 0) return "Please select at least one item for the claim.";
      if (!unboxingFile) return "Please attach unboxing video.";
    }

    // packaging is always required (but proof type will differ for weight disputes)
    if (!packagingFile) {
      return claimType === "weight_dispute"
        ? "Please attach packaging video (ORDER_PACKAGING) for weight dispute."
        : "Please attach packaging video.";
    }

    if (claimType !== "weight_dispute" && (!claimDescription || claimDescription.trim().length < 10)) return "Please provide a short description (min 10 chars).";
    if (claimType === "weight_dispute" && (!claimDescription || claimDescription.trim().length < 5)) return "Please describe the weight dispute briefly (min 5 chars).";

    const check = (f, allowedTypes, name) => {
      if (!f) return true;
      if (f.size > MAX_BYTES) return `${name} is too large. Max ${humanFileSize(MAX_BYTES)}.`;
      if (allowedTypes && !allowedTypes.includes(f.type)) return `${name} has unsupported type ${f.type}.`;
      return true;
    };

    let ok = check(unboxingFile, VIDEO_TYPES, "Unboxing video"); if (ok !== true) return ok;
    ok = check(packagingFile, VIDEO_TYPES, "Packaging video"); if (ok !== true) return ok;
    ok = check(podFile, POD_TYPES.concat(VIDEO_TYPES), "Proof of delivery"); if (ok !== true) return ok;
    return true;
  };

  const computeEta = (pct) => {
    if (!uploadStartRef.current) return null;
    const elapsed = (Date.now() - uploadStartRef.current) / 1000; // seconds
    const delta = Math.max(1, pct - lastPctRef.current);
    if (delta <= 0) return null;
    const rate = delta / elapsed;
    if (!rate || rate <= 0) return null;
    const remainPct = 100 - pct;
    const secs = Math.round(remainPct / rate);
    return secs > 0 ? fmtDuration(secs) : "00:00";
  };

  useEffect(() => {
    if (!resultModalOpen) return;
    if (resultKind !== 'success') {
      setResultCountdown(0);
      return;
    }
    setResultCountdown(5);
    const iv = setInterval(() => {
      setResultCountdown(c => {
        if (c <= 1) {
          clearInterval(iv);
          setResultModalOpen(false);
          if (resultKind === 'success') {
            const payload = resultData;
            setResultData(null);
            onClose?.({ success: true, data: payload });
          }
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(iv);
  }, [resultModalOpen, resultKind, resultData, onClose]);

  const finalizeCloseNow = () => {
    setResultModalOpen(false);
    const payload = resultData;
    setResultData(null);
    if (resultKind === 'success') onClose?.({ success: true, data: payload });
  };

  const copyToClipboard = async (text) => { try { await navigator.clipboard.writeText(text || ''); } catch (e) { console.warn('copy failed', e); } };

  const doSubmit = async () => {
    setError(""); setSuccessMessage("");
    // reset ETA
    uploadStartRef.current = Date.now();
    lastPctRef.current = 0;

    for (const k of ['unboxing', 'packaging', 'pod']) {
      if (fileStatus[k]?.status === 'oversized') { setError('One or more files exceed the size limit. Please confirm to upload or remove the file.'); return; }
    }

    abortControllerRef.current = new AbortController();
    setBusy(true);
    setOverallPct(2);

    try {
      const itemsPayload = (selectedItems || []).map(si => ({
        sku_id: si?.item_general_details?.sku ?? "",
        item_name: si?.item_general_details?.item_name ?? "",
        units: si?.quantity ?? 1,
      }));

      const claimPayload = {
        merchant_id: merchantId,
        awb_number: awbNumber,
        order_reference_number: orderReferenceNumber || "",
        claim_reason: claimDescription ? claimDescription.substring(0, 120) : "claim_added",
        claim_description: claimType === "weight_dispute" ? "Shipment Dimension Proof" : claimDescription,
        items: itemsPayload,
        claim_type: claimType === "weight_dispute" ? "WEIGHT_DISPUTE" : "DAMAGE",
        claim_reason_code: claimReasonCode ?? null,
      };

      // API: create claim
      let createdClaim = null;
      try {
        createdClaim = await createClaim(claimPayload);
      } catch (err) {
        if (err.code === "DUPLICATE_RECORD" || err.message?.includes("Claim already exists")) {
          console.warn("Claim already exists, fetching existing claim...");
          const existing = await apiClient.get(API.getClaimsList(), {
            params: { awb: awbNumber, merchantId },
          });
          if (Array.isArray(existing.data) && existing.data.length > 0) {
            createdClaim = existing.data[0];
          } else {
            throw err;
          }
        } else {
          throw err;
        }
      }

      const claimId = createdClaim?.claim_id ?? createdClaim?.claimId ?? createdClaim?.id;
      if (!claimId) throw new Error('Claim creation returned unexpected response (no claim id).');
      currentClaimIdRef.current = claimId;

      const proofPurposeFor = (fileRole) => {
        if (fileRole === 'packaging') {
          return claimType === "weight_dispute" ? 'DIMENSION_PROOF' : 'DAMAGE_PROOF';
        }
        if (fileRole === 'unboxing') return 'DAMAGE_PROOF';
        if (fileRole === 'pod') return 'POD_PROOF';
        return 'OTHER';
      };

      const uploadOne = async (fileKey, file, proofType) => {
        if (!file) return null;
        setFileStatusSafe(fileKey, { status: 'init', pct: 2, message: 'Initializing proof' });
        const orderPlacedRaw = shipment?.order_placed_date_time ?? null;
        const orderPlacedIso = orderPlacedRaw ? new Date(orderPlacedRaw).toISOString() : null;

        const initBody = {
          merchant_id: merchantId,
          awb_number: awbNumber,
          order_reference_number: orderReferenceNumber || "",
          file_name: file.name,
          file_size: file.size,
          content_type: file.type,
          proof_type: proofType,
          proof_purpose: proofPurposeFor(fileKey),
          claim_type: claimType === "weight_dispute" ? "WEIGHT_DISPUTE" : "DAMAGE",
          ...(claimId ? { claim_id: claimId } : {}),
          ...(orderPlacedIso ? { order_placed_date_time: orderPlacedIso } : {}),
        };


        // API: init proof
        const initResp = await initProof(initBody);
        const proofId = initResp?.proof_id ?? initResp?.proofId;
        const parts = initResp?.parts ?? initResp?.parts_list ?? initResp?.parts;
        if (!proofId || !Array.isArray(parts) || parts.length === 0) {
          throw new Error("Invalid init response from server for proof upload");
        }

        persistInit(fileKey, initResp);
        setFileStatusSafe(fileKey, { status: 'uploading', pct: 3, message: 'Uploading parts' });

        const result = await uploadFileAndComplete({
          file,
          initResp,
          merchantId,
          claimId,
          signal: abortControllerRef.current?.signal,
          onProgress: (pct) => {
            setFileStatusSafe(fileKey, { pct });
            setFileStatus(prev => {
              const cur = {
                unboxing: fileKey === "unboxing" ? pct : (prev.unboxing?.pct ?? 0),
                packaging: fileKey === "packaging" ? pct : (prev.packaging?.pct ?? 0),
                pod: fileKey === "pod" ? pct : (prev.pod?.pct ?? 0),
              };
              const avg = Math.round((cur.unboxing + cur.packaging + cur.pod) / 3);
              lastPctRef.current = avg;
              setOverallPct(Math.max(5, avg));
              return prev;
            });
          },
          concurrency: 3,
          maxRetries: file.size > 50 * 1024 * 1024 ? 5 : 3,
        });

        const normalized = {
          proofId: result?.proof_id ?? result?.proofId ?? proofId,
          uploadId: initResp?.uploadId ?? initResp?.upload_id ?? (result?.uploadId ?? null),
          key: initResp?.key ?? initResp?.object_key ?? initResp?.key,
          proofPurpose: initResp?.proofPurpose ?? initBody.proofPurpose,
        };

        setFileStatusSafe(fileKey, { status: 'done', pct: 100, message: 'Uploaded and attached' });
        clearPersist(fileKey);
        return normalized;
      };

      const uploaded = {};
      if (claimType !== "weight_dispute") {
        uploaded.unboxing = await uploadOne('unboxing', unboxingFile, 'UNBOXING_VIDEO');
      }
      const packagingProofType = claimType === "weight_dispute" ? "ORDER_PACKAGING" : "PACKAGING_VIDEO";
      uploaded.packaging = await uploadOne('packaging', packagingFile, packagingProofType);
      if (podFile) uploaded.pod = await uploadOne('pod', podFile, 'POD_FILE');

      setOverallPct(100);
      setBusy(false);

      // attach server-returned proof metadata into UI success payload
      const payload = {
        claim: createdClaim,
        proofs: uploaded
      };
      setSuccessPayload(payload);
      setSuccessMessage('Claim submitted and proofs uploaded successfully.');

      // Show enterprise result modal (success)
      setResultKind('success');
      setResultData(payload);
      setResultModalOpen(true);

    } catch (err) {
      console.error("Upload flow error", err);

      let errObj = null;
      if (err && err.response && err.response.data) {
        errObj = err.response.data;
      } else if (err && err.data) {
        errObj = err.data;
      } else {
        // fallback
        errObj = {
          code: err?.code ?? 'internal_error',
          message: err?.message ?? (typeof err === 'string' ? err : String(err)),
        };
      }

      setError(errObj.message || 'Upload failed');

      setResultKind('error');
      setResultData(errObj);
      setResultModalOpen(true);

      setBusy(false);
    } finally {
      if (abortControllerRef.current) abortControllerRef.current = null;
    }
  };


  const onUploadClick = () => {
    const v = validateForm();
    if (v !== true) {
      setError(v);
      return;
    }
    setConfirmOpen(true);
  };

  const onConfirmProceed = async () => {
    setConfirmOpen(false);
    uploadStartRef.current = Date.now();
    lastPctRef.current = overallPct ?? 0;
    await doSubmit();
  };

  const abortAll = async () => {
    try {
      if (abortControllerRef.current) abortControllerRef.current.abort();
      setBusy(false);
      setOverallPct(0);
      setError('Upload aborted by user.');

      try {
        await abortUpload({ merchant_id: merchantId, awb_number: awbNumber, claim_id: currentClaimIdRef.current });
      } catch (e) {
        console.warn('abortUpload API failed', e);
      }

      // reset statuses
      setFileStatus({
        unboxing: { status: 'cancelled', pct: 0, message: 'Cancelled' },
        packaging: { status: 'cancelled', pct: 0, message: 'Cancelled' },
        pod: { status: 'cancelled', pct: 0, message: 'Cancelled' },
      });

      currentClaimIdRef.current = null;
    } catch (e) {
      console.error('Error aborting', e);
    }
  };

  const cancel = () => {
    if (busy) {
      abortAll();
      return;
    }
    onClose?.();
  };

  const UploadCard = ({ title, hint, file, setFile, keyName, accept, required }) => {
    const s = fileStatus[keyName] || {};

    const inputId = `file-${keyName}`;

    return (
      <motion.div
        whileHover={{ y: -3 }}
        className="rounded-lg border border-gray-200 p-3 bg-white shadow-sm"
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <div className="text-sm font-semibold flex gap-1">{title}{required ? <div className="text-red-500"> * </div> : ''}</div>
              {s.status === 'oversized' && (
                <div className="ml-2 inline-flex items-center gap-1 rounded-full bg-yellow-50 text-yellow-700 px-2 py-0.5 text-xs">
                  Oversized
                </div>
              )}
            </div>
            <div className="text-xs text-gray-500 mt-1">{hint}</div>
          </div>

          <div className="w-8 h-8" />
        </div>

        <label
          htmlFor={inputId}
          onDrop={handleDrop(makeHandleFileSelect(setFile, keyName))}
          onDragOver={(e) => e.preventDefault()}
          className={`
            mt-3 grid grid-cols-12 gap-3 items-start
            cursor-pointer select-none
          `}
          aria-label={`Upload ${title}`}
        >
          <input
            id={inputId}
            type="file"
            accept={accept}
            onChange={makeHandleFileSelect(setFile, keyName)}
            className="hidden"
          />

          <div className="col-span-12 rounded bg-gradient-to-br from-gray-50 to-white border border-dashed p-4 flex items-center justify-center min-h-[100px] relative">
            {!file ? (
              <div className="text-center text-sm text-gray-500">
                <div className="mx-auto mb-2">
                  <InlineUploadIllustration className="w-16 h-16" />
                </div>
                <div className="font-medium">Drop file here or click to upload</div>
                <div className="mt-1 text-xxs text-gray-400">{hint}</div>
              </div>
            ) : (
              <div className="w-full flex items-center gap-3 relative">
                {/* preview area */}
                <div className="flex-1 min-w-0">
                  {file.type.startsWith('image/') ? (
                    <div className="relative rounded overflow-hidden shadow-sm">
                      <img
                        src={fileUrls[keyName]}
                        alt={file.name}
                        className="w-full max-h-40 object-contain block"
                      />
                    </div>
                  ) : file.type.startsWith('video/') ? (
                    <div className="relative rounded overflow-hidden shadow-sm">
                      <video
                        controls
                        className="w-full max-h-40 rounded"
                        src={fileUrls[keyName]}
                      >
                        <source src={fileUrls[keyName]} type={file.type} />
                        Your browser doesn't support preview.
                      </video>

                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          try { e.currentTarget.blur(); } catch (err) { /* ignore */ }
                          if (fileUrls[keyName]) window.open(fileUrls[keyName], "_blank");
                        }}
                        className="absolute inset-0 m-auto w-12 h-12 rounded-full bg-black/50 flex items-center justify-center text-white hover:bg-black/60"
                        aria-label="Play preview"
                        style={{ top: '50%', transform: 'translateY(-50%)' }}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                          <path d="M5 3v18l15-9L5 3z" fill="currentColor" />
                        </svg>
                      </button>

                    </div>
                  ) : (
                    <div className="mt-2 text-xxs text-gray-500">
                      <div className="truncate">{file.name}</div>
                    </div>
                  )}
                </div>

                {/* status chip */}
                <div className="flex flex-col items-end gap-2">
                  <div className="text-xxs text-gray-500">{s.status}</div>
                  <div className="text-xxs text-gray-500">{s.pct ?? 0}%</div>
                </div>

                {/* small X (remove) */}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); removeFile(keyName); }}
                  aria-label={`Remove ${title}`}
                  className="absolute right-2 top-2 inline-flex items-center justify-center rounded-full bg-white border shadow-sm w-7 h-7 text-gray-500 hover:bg-gray-50"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

            )}
          </div>
        </label>

        <div className="col-span-12 mt-3">
          <div className="flex items-center justify-between text-xs">
            <div className="text-sm text-gray-700 truncate">{file?.name ?? 'No file selected'}</div>
            <div className="text-xxs text-gray-500">{file ? humanFileSize(file.size) : '—'}</div>
          </div>

          {s.status === 'uploading' && (
            <div className="mt-2 w-full bg-gray-100 h-2 rounded overflow-hidden">
              <div
                style={{
                  width: `${s.pct ?? 0}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, rgba(99,102,241,1), rgba(236,72,153,1))',
                  transition: 'width 300ms ease'
                }}
                aria-hidden
              />
            </div>
          )}

          <div className="mt-2 flex items-center gap-2 justify-end">
            {s.status === 'error' && (
              <button onClick={() => handleRetry(keyName)} className="text-xs px-2 py-1 rounded bg-red-50 text-red-700">Retry</button>
            )}
          </div>
        </div>
      </motion.div >
    );
  };


  const ItemList = () => {
    if (!items || items.length === 0) {
      return <div className="text-sm text-gray-500">No items found for this shipment.</div>;
    }
    return (
      <div className="rounded-lg border p-2 bg-white">
        <div className="flex items-center justify-between mb-1">
          <div className="text-sm font-medium">Select item(s) to claim</div>
          <div className="text-xxs text-gray-500">{selectedItems.length} selected</div>
        </div>

        <div className="grid gap-1 max-h-28 overflow-auto">
          {items.map((it, idx) => {
            const sku = it.item_general_details?.sku ?? `item-${idx}`;
            const name = it.item_general_details?.item_name ?? "—";
            const qty = it.quantity ?? 1;
            const checked = isItemSelected(it);
            return (
              <label key={idx} className={`flex items-center gap-3 px-2 py-1 rounded hover:bg-indigo-50 cursor-pointer ${checked ? 'ring-1 ring-indigo-100 bg-indigo-50' : ''}`}>
                <input type="checkbox" checked={checked} onChange={() => toggleSelectItem(it)} className="h-4 w-4 accent-indigo-600" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{sku} — {name}</div>
                  <div className="text-xxs text-gray-500">Qty: {qty}</div>
                </div>
                <div className="text-xxs text-gray-400">Click to {checked ? 'remove' : 'select'}</div>
              </label>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <>
      { }
      <style>{`
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>

      <Transition appear show={open} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => { if (!busy) onClose?.(); }}>
          <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-start justify-center p-6 text-center sm:items-center sm:p-10">
              <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 translate-y-6 sm:translate-y-0 sm:scale-95" enterTo="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 translate-y-0 sm:scale-100" leaveTo="opacity-0 translate-y-6 sm:translate-y-0 sm:scale-95">
                <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white p-5 text-left align-middle shadow-2xl transition-all">
                  {/* HEADER + FORM (adjust title for claimType) */}
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <InlineAppLogo />
                      <div>
                        <Dialog.Title as="h3" className="flex items-center gap-2 text-lg font-semibold text-slate-900">
                          <UploadCloud className="w-5 h-5 text-indigo-600" />
                          {claimType === "weight_dispute" ? "Upload Dimensions Proof" : "Upload Damaged Claim Proofs"}
                        </Dialog.Title>
                        <p className="mt-0.5 text-xs text-gray-500">
                          {claimType === "weight_dispute" ? "" : "Attach unboxing & packaging videos and POD."}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <div className="text-xs text-gray-500">Need help? <a href="#" onClick={(e) => { e.preventDefault(); alert('Open docs'); }} className="text-indigo-600">support@wayuvega.com</a></div>
                      <button onClick={() => { if (!busy) onClose?.(); }} aria-label="Close" className="rounded-md p-2 hover:bg-gray-100"><X className="w-4 h-4 text-gray-600" /></button>
                    </div>
                  </div>

                  {/* TOP AREA */}
                  <div className="mt-4 rounded-lg border bg-gray-50 p-3 shadow-sm space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xxs font-medium text-gray-600">AWB <span className="text-red-600">*</span></label>
                        <input value={awbNumber} onChange={(e) => setAwbNumber(e.target.value)} className="mt-1 w-full rounded border px-2 py-1 text-sm shadow-sm bg-white" />
                      </div>
                      <div>
                        <label className="block text-xxs font-medium text-gray-600">Order reference</label>
                        <input value={orderReferenceNumber} onChange={(e) => setOrderReferenceNumber(e.target.value)} className="mt-1 w-full rounded border px-2 py-1 text-sm shadow-sm bg-white" />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xxs font-medium text-gray-600">Items</label>
                      <div className="mt-2"><ItemList /></div>
                    </div>
                    {/* Hide description input for weight_dispute */}
                    {claimType !== "weight_dispute" && (
                      <div>
                        <label className="block text-xxs font-medium text-gray-600">Short description</label>
                        <textarea
                          value={claimDescription}
                          onChange={(e) => setClaimDescription(e.target.value)}
                          rows={2}
                          className="mt-1 w-full rounded border px-2 py-1 text-sm shadow-sm bg-white"
                        />
                      </div>
                    )}
                    {/* Show Reason only for non-weight_dispute claims */}
                    {claimType !== "weight_dispute" && (
                      <div>
                        <label className="block text-xxs font-medium text-gray-600">Reason</label>
                        <div className="mt-1">
                          {reasonsLoading ? (
                            <div className="text-xxs text-gray-500">Loading reasons…</div>
                          ) : reasonsError ? (
                            <div className="text-xxs text-red-600">Failed to load reasons</div>
                          ) : (
                            <select
                              value={claimReasonCode ?? ""}
                              onChange={(e) => setClaimReasonCode(e.target.value)}
                              className="mt-1 w-full rounded border px-2 py-1 text-sm shadow-sm bg-white"
                            >
                              <option value="" disabled>Select reason</option>
                              {claimReasonsList.map((r) => (
                                <option key={r.code} value={r.code}>
                                  {r.description || r.reason || r.code}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                      </div>
                    )}
                    {/* Tips */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 text-xs">
                      <div className="rounded-lg border p-2 bg-white">
                        <div className="font-medium">Tip 1</div>
                        <div className="text-gray-500 mt-1">Select one or more items above - each selected item will be attached to this claim.</div>
                      </div>
                      <div className="rounded-lg border p-2 bg-white">
                        <div className="font-medium">Tip 2</div>
                        <div className="text-gray-500 mt-1">{claimType === "weight_dispute" ? "Packaging video (ORDER_PACKAGING) is required for weight disputes." : "Unboxing + Packaging videos are required; POD is optional but recommended."}</div>
                      </div>
                      <div className="rounded-lg border p-2 bg-white">
                        <div className="font-medium">Tip 3</div>
                        <div className="text-gray-500 mt-1">Please ensure the AWB is clearly visible in videos. Claims may be rejected if AWB is not readable.</div>
                      </div>
                      <div className="rounded-lg border p-2 bg-white">
                        <div className="font-medium">Tip 4</div>
                        <div className="text-gray-500 mt-1">You can preview files before submitting.</div>
                      </div>
                    </div>
                  </div>

                  {/* Upload cards */}
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                    {claimType === "weight_dispute" ? (
                      <div className="md:col-span-3">
                        <UploadCard
                          title="Packaging video (ORDER_PACKAGING)"
                          hint={`MP4 / MOV / WEBM — max ${humanFileSize(MAX_BYTES)}`}
                          file={packagingFile}
                          setFile={setPackagingFile}
                          keyName="packaging"
                          accept="video/*"
                          required
                        />
                      </div>
                    ) : (
                      <>
                        <UploadCard title="Unboxing video" hint={`MP4 / MOV / WEBM — max ${humanFileSize(MAX_BYTES)}`} file={unboxingFile} setFile={setUnboxingFile} keyName="unboxing" accept="video/*" required />
                        <UploadCard title="Packaging video" hint={`MP4 / MOV / WEBM — max ${humanFileSize(MAX_BYTES)}`} file={packagingFile} setFile={setPackagingFile} keyName="packaging" accept="video/*" required />
                        <UploadCard title="Proof of delivery" hint="Image / PDF — optional but recommended" file={podFile} setFile={setPodFile} keyName="pod" accept="image/*,application/pdf,video/*" />
                      </>
                    )}
                  </div>

                  <div className="mt-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-xs font-medium text-gray-700">Upload progress</div>
                      <div className="text-xxs text-gray-500">{overallPct}%</div>
                    </div>

                    <div
                      role="progressbar"
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-valuenow={overallPct}
                      className="w-full bg-gray-100 rounded-full h-3 overflow-hidden"
                      title={`Overall progress ${overallPct}%`}
                    >
                      <div
                        style={{
                          width: `${overallPct}%`,
                          height: '100%',
                          borderRadius: 999,
                          backgroundImage: 'linear-gradient(90deg, rgba(99,102,241,1) 0%, rgba(168,85,247,1) 40%, rgba(236,72,153,1) 100%)',
                          backgroundSize: '200% 100%',
                          animation: overallPct < 100 ? 'gradientShift 2.4s linear infinite' : 'none',
                          transition: 'width 350ms ease'
                        }}
                      />
                    </div>

                    <div className="mt-2 text-xxs text-gray-500 text-right">
                      {busy && overallPct < 100 ? (
                        <span>ETA: {computeEta(overallPct) ?? 'estimating...'}</span>
                      ) : (
                        <span>{overallPct === 100 ? 'Upload complete' : ''}</span>
                      )}
                    </div>
                  </div>

                  {/* CONTROLS - moved BELOW progress bar */}
                  <div className="mt-4 flex flex-col sm:flex-row gap-2">
                    <button onClick={onUploadClick} disabled={busy} className={`flex-1 inline-flex items-center justify-center gap-2 rounded bg-indigo-600 px-3 py-2 text-sm text-white ${busy ? 'opacity-60 cursor-not-allowed' : 'hover:bg-indigo-700'}`}>
                      <UploadCloud className="w-4 h-4" /> {busy ? 'Uploading...' : 'Upload Proofs & Create Claim'}
                    </button>
                    <button onClick={cancel} className="flex-1 rounded border px-3 py-2 text-sm bg-white">{busy ? 'Abort' : 'Close'}</button>
                  </div>

                  {/* Footer messages */}
                  <div className="mt-4">
                    {error && <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 flex items-center gap-2"><AlertTriangle />{error}</div>}
                    {successMessage && <div className="rounded-md bg-emerald-50 p-3 text-sm text-emerald-700 flex items-center gap-2"><Check />{successMessage}</div>}
                  </div>

                  {/* Oversize modal */}
                  <Transition show={oversizeModal.open} as={Fragment}>
                    <Dialog as="div" className="fixed inset-0 z-60 flex items-center justify-center p-6" onClose={() => setOversizeModal({ open: false, key: null, file: null, message: '' })}>
                      <div className="relative w-full max-w-2xl">
                        <div className="rounded-2xl bg-white p-6 shadow-2xl border">
                          <div className="flex items-start gap-4">
                            <div className="rounded-full bg-yellow-50 p-3"><AlertTriangle className="w-6 h-6 text-yellow-600" /></div>
                            <div className="flex-1">
                              <div className="text-lg font-semibold">Large file detected</div>
                              <div className="mt-2 text-sm text-gray-700">{oversizeModal.message}</div>
                              <div className="mt-3 text-sm text-gray-600">Large uploads can take longer on mobile networks. Choose an option below:</div>
                              <div className="mt-4 flex gap-3">
                                <button onClick={approveOversize} className="rounded px-4 py-2 bg-yellow-600 text-white">Upload anyway</button>
                                <button onClick={() => alert('Client-side compression coming soon')} className="rounded px-4 py-2 border">Compress & upload</button>
                                <a href="#" onClick={(e) => { e.preventDefault(); alert('Desktop uploader coming soon'); }} className="rounded px-4 py-2 border">Use desktop uploader</a>
                                <button onClick={cancelOversize} className="ml-auto rounded px-4 py-2 border">Cancel</button>
                              </div>
                              <div className="mt-3 text-xxs text-gray-500">We’ll remember your choice for this file during this session.</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Dialog>
                  </Transition>

                  {/* Confirm upload modal */}
                  <Transition show={confirmOpen} as={Fragment}>
                    <Dialog as="div" className="fixed inset-0 z-[70] flex items-center justify-center p-6 pointer-events-auto" onClose={() => setConfirmOpen(false)}>
                      <div className="relative w-full max-w-xl">
                        <div className="rounded-2xl bg-white p-6 shadow-2xl border">
                          <div className="flex items-start gap-4">
                            <div className="flex-1">
                              <h3 className="text-lg font-semibold">Confirm upload</h3>
                              <p className="mt-2 text-sm text-gray-700">Once you start uploading, files can't be changed. Please ensure each video and image:</p>
                              <ul className="mt-2 list-disc ml-5 text-sm text-gray-600">
                                <li>Clearly shows the item and packaging from multiple angles.</li>
                                <li>Is well-lit, in focus, and long enough to show the issue.</li>
                                <li>Doesn't contain sensitive or unrelated information.</li>
                                <li>Please ensure the AWB is clearly visible in both videos. Claims may be rejected if the AWB is not readable.</li>
                              </ul>
                              <p className="mt-2 text-sm text-gray-700">If you understand and want to proceed, click <strong>Proceed</strong>.</p>

                              <div className="mt-4 flex gap-2 justify-end">
                                <button type="button" onClick={() => setConfirmOpen(false)} className="rounded px-4 py-2 border">Cancel</button>
                                <button
                                  type="button"
                                  onClick={async () => {
                                    console.debug('[ClaimUpload] Proceed clicked (confirm modal)');
                                    setConfirmOpen(false);
                                    await new Promise(r => setTimeout(r, 80));
                                    onConfirmProceed();
                                  }}
                                  className="rounded px-4 py-2 bg-indigo-600 text-white hover:bg-indigo-700"
                                >
                                  Proceed
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Dialog>
                  </Transition>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      <Transition appear show={resultModalOpen} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-[90] flex items-center justify-center p-6"
          onClose={() => {
            if (resultKind === 'error') {
              setResultModalOpen(false);
            }
          }}
        >
          {/* backdrop with slightly lower z so panel (z-90) stays above */}
          <div className="absolute inset-0 bg-black/40 z-[80]" />
          <div className="relative w-full max-w-2xl z-[90]">
            <div className="rounded-2xl bg-white p-6 shadow-2xl border text-center z-[90]">
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.28 }}>
                {resultKind === 'success' ? (
                  <div className="w-full max-w-3xl">
                    {/* Header */}
                    <div className="flex items-center gap-4 justify-start">
                      <div className="rounded-full bg-emerald-50 p-3 flex items-center justify-center shadow-sm">
                        <Check className="w-6 h-6 text-emerald-600" />
                      </div>
                      <div>
                        <div className="text-lg font-semibold text-emerald-700">Claim submitted</div>
                        <div className="text-sm text-gray-500">We received your claim and proofs — we'll review it shortly.</div>
                      </div>
                    </div>

                    {/* Main card */}
                    <div className="mt-4 rounded-lg bg-white border shadow-sm p-5">
                      {/* Claim ID row */}
                      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-gray-400">Claim ID</div>
                          <div className="mt-2 flex items-center gap-3">
                            <div className="font-mono text-sm text-slate-900 break-words select-all">
                              {resultData?.claim?.claim_id ?? resultData?.claim?.claimId ?? resultData?.claim?.id ?? '—'}
                            </div>

                            <button
                              onClick={() => copyToClipboard(resultData?.claim?.claim_id ?? resultData?.claim?.claimId ?? resultData?.claim?.id ?? '')}
                              className="inline-flex items-center gap-2 text-xs rounded px-2 py-1 border bg-gray-50 hover:bg-gray-100"
                              aria-label="Copy claim id"
                            >
                              <Copy className="w-3 h-3" /> Copy
                            </button>
                            <button
                              onClick={() => {
                                const id = resultData?.claim?.claim_id ?? resultData?.claim?.claimId ?? resultData?.claim?.id;
                                navigate(`/claim-tracker?claimId=${encodeURIComponent(id)}`);
                              }}
                              className="inline-flex items-center gap-2 rounded px-4 py-2 bg-indigo-600 text-white hover:bg-indigo-700"
                            >
                              View claim
                            </button>
                          </div>
                        </div>

                        <div className="flex-shrink-0 text-right">
                          <div className="text-xs text-gray-400">Status</div>
                          <div className="mt-2 inline-flex items-center px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-sm font-medium">Submitted</div>
                        </div>
                      </div>

                      {/* Info chips (Type / Reason / Proofs) */}
                      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="rounded-lg border bg-gray-50 p-3 text-center">
                          <div className="text-xxs text-gray-400">Type</div>
                          <div className="mt-1 font-medium">{resultData?.claim?.claim_type ?? resultData?.claim?.claimType ?? '—'}</div>
                        </div>
                        <div className="rounded-lg border bg-gray-50 p-3 text-center">
                          <div className="text-xxs text-gray-400">Reason</div>
                          <div className="mt-1 font-medium">{resultData?.claim?.claim_reason_code ?? resultData?.claim?.claimReasonCode ?? '—'}</div>
                        </div>
                        <div className="rounded-lg border bg-gray-50 p-3 text-center">
                          <div className="text-xxs text-gray-400">Proofs</div>
                          <div className="mt-1 font-medium">{resultData?.proofs ? Object.keys(resultData.proofs).length : 0}</div>
                        </div>
                      </div>

                      {/* Proof chips (compact horizontal list; no "Uploaded proofs" header) */}
                      <div className="mt-4">
                        <div className="flex gap-2 overflow-x-auto py-1">
                          {resultData?.proofs && Object.entries(resultData.proofs).length > 0 ? (
                            Object.entries(resultData.proofs).map(([k, v]) => {
                              const title = k.replace(/_|-/g, ' ').toUpperCase();
                              const meta = v?.uploadId ?? v?.upload_id ?? v?.proofId ?? v?.key ?? '--';
                              const truncated = String(meta).length > 30 ? String(meta).slice(0, 28) + '…' : String(meta);
                              return (
                                <div key={k} className="flex-none w-[min(46%,420px)] sm:w-[280px] rounded-lg border bg-white p-3 shadow-sm flex items-center justify-between gap-3">
                                  <div className="min-w-0">
                                    <div className="text-sm font-medium truncate">{title}</div>
                                    <div className="mt-1 text-xxs text-gray-500 font-mono truncate">{truncated}</div>
                                    <div className="mt-1 text-xxs text-gray-400">{v?.proofPurpose ?? ''}</div>
                                  </div>
                                  <div className="flex flex-col items-end gap-2">
                                    {v?.key && (
                                      <button
                                        onClick={() => {
                                          // customize to open presigned url or file viewer
                                          window.open(`/files/${encodeURIComponent(v.key)}`, '_blank');
                                        }}
                                        className="text-xxs px-2 py-1 border rounded bg-white hover:bg-gray-50"
                                      >
                                        Open
                                      </button>
                                    )}
                                    <button
                                      onClick={() => copyToClipboard(JSON.stringify(v, null, 2))}
                                      className="text-xxs px-2 py-1 border rounded bg-white hover:bg-gray-50"
                                    >
                                      Copy
                                    </button>
                                  </div>
                                </div>
                              );
                            })
                          ) : (
                            <div className="text-sm text-gray-500">No proof metadata returned.</div>
                          )}
                        </div>
                      </div>

                      {/* Footer actions */}
                      <div className="mt-5 flex items-center justify-end gap-3">
                        <button
                          onClick={() => {
                            const id = resultData?.claim?.claim_id ?? resultData?.claim?.claimId ?? resultData?.claim?.id;
                            if (id) window.open(`/claim-tracker?claimId=${encodeURIComponent(id)}`, "_blank");
                          }}
                          className="inline-flex items-center gap-2 rounded px-4 py-2 bg-indigo-600 text-white hover:bg-indigo-700"
                        >
                          View claim
                        </button>

                        <button onClick={finalizeCloseNow} className="inline-flex items-center gap-2 rounded px-4 py-2 border bg-white hover:bg-gray-50">
                          Close now
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  // ERROR UI - DOES NOT AUTO-CLOSE
                  <>
                    <div className="mx-auto w-28 h-28 flex items-center justify-center">
                      <div className="rounded-full bg-red-50 w-20 h-20 flex items-center justify-center"><AlertTriangle className="w-8 h-8 text-red-600" /></div>
                    </div>
                    <div className="mt-4 text-xl font-semibold text-red-700">Upload failed</div>
                    <div className="mt-2 text-sm text-gray-700">An error occurred while creating the claim or uploading proofs.</div>

                    <div className="mt-4 text-left">
                      <div className="text-sm font-medium">Error details</div>
                      <div className="mt-2 text-sm text-gray-600">
                        <div><strong>Code:</strong> {resultData?.code ?? '—'}</div>
                        <div className="mt-1"><strong>Message:</strong> {resultData?.message ?? (typeof resultData === 'string' ? resultData : '—')}</div>
                        {resultData?.correlation_id && (<div className="mt-1"><strong>Correlation id:</strong> <span className="font-mono text-xs">{resultData.correlation_id}</span>
                          <button onClick={() => copyToClipboard(resultData.correlation_id)} className="ml-2 p-1 rounded hover:bg-gray-100"><Copy className="w-4 h-4" /></button>
                        </div>)}
                        {resultData?.context_data && (
                          <div className="mt-2">
                            <div className="text-xs text-gray-500">Context:</div>
                            <pre className="mt-1 text-xs bg-gray-50 p-2 rounded overflow-auto">{JSON.stringify(resultData.context_data, null, 2)}</pre>
                          </div>
                        )}
                      </div>

                      <div className="mt-4 flex justify-end gap-2">
                        <button onClick={() => { setResultModalOpen(false); setError(resultData?.message ?? ''); }} className="rounded px-4 py-2 border">Dismiss</button>
                        <button onClick={() => { setResultModalOpen(false); }} className="rounded px-4 py-2 bg-indigo-600 text-white hover:bg-indigo-700">Close</button>
                      </div>
                    </div>
                  </>
                )}
              </motion.div>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
